import { MAX_TAG_LENGTH } from "./tag-bounds";

/** Both alternatives bound their forward scan for a close — see MAX_TAG_LENGTH. */
const TEMPLATE_TAG_RE = new RegExp(
  [
    String.raw`\{\{-?(?=[\s\S]{0,${MAX_TAG_LENGTH}}?-?\}\})[\s\S]*?-?\}\}`,
    String.raw`\{%-?(?=[\s\S]{0,${MAX_TAG_LENGTH}}?-?%\})[\s\S]*?-?%\}`,
  ].join("|"),
  "g",
);
const ELSEIF_TAG_RE = /(\{%-?\s*)elseif(?=\s)/g;
const SET_TAG_RE = /(\{%-?\s*)set(\s+[A-Za-z_][A-Za-z0-9_]*\s*=)/g;

/**
 * A pipe-prefixed filter call with parenthesized arguments. The leading pipe is
 * what keeps a Twig *function* call (`snippet('welcome')`) out of the match —
 * only filters take Liquid's colon form. A pipe *inside* the arguments (string
 * literals are already masked) means a nested filter chain like
 * `default(y|upper)`, whose colon form Liquid would chain on the input instead
 * of the argument — refuse the match so the parse failure stays the signal.
 */
const FILTER_CALL_RE = /\|\s*([A-Za-z_][A-Za-z0-9_]*)\s*\(([^()|]*)\)/g;

const STRING_LITERAL_RE = /"[^"]*"|'[^']*'/g;

/** No pipe or parenthesis, so a masked span can never look like filter syntax. */
const LITERAL_PLACEHOLDER_RE = /__ht_twig_literal_(\d+)__/g;

/**
 * String literals are masked before the rewrite and restored after, so a pipe
 * or parenthesis inside quotes is never mistaken for filter syntax.
 */
function rewriteFilterCalls(tag: string): string {
  const literals: string[] = [];
  const masked = tag.replace(STRING_LITERAL_RE, (literal) => {
    literals.push(literal);
    return `__ht_twig_literal_${literals.length - 1}__`;
  });
  const rewritten = masked.replace(
    FILTER_CALL_RE,
    (_match, name: string, args: string) =>
      args.trim() === "" ? `| ${name}` : `| ${name}: ${args}`,
  );
  return rewritten.replace(LITERAL_PLACEHOLDER_RE, (match, index: string) => {
    const literal = literals[Number(index)];
    // Placeholder-shaped text the author wrote themselves is not ours to touch.
    return literal ?? match;
  });
}

/**
 * Rewrites the Twig 3.8 (Xtremepush) constructs that have an exact Liquid
 * equivalent, so a Liquid engine can parse what Twig authors wrote: `elseif`
 * to `elsif`, inline `set` to `assign`, and parenthesized filter arguments
 * (`|default('there')`) to colon form (`| default: 'there'`). Twig with no
 * Liquid spelling (`{% verbatim %}`, arrow-function filters) is left alone —
 * the caller's parse failure stays the honest signal for those.
 */
export function twigToLiquid(template: string): string {
  return template
    .replace(ELSEIF_TAG_RE, "$1elsif")
    .replace(SET_TAG_RE, "$1assign$2")
    .replace(TEMPLATE_TAG_RE, rewriteFilterCalls);
}
